/*
SYNCS a CAD node (conceptual map) with Three.js objects.

It only creates/modifies three.js objects when their "peer" object in the conceptual model has changed.

In order to do deep tree comparison quickly, it uses immutable objects so that we can compare references.

There are different things to sync

a) Globals:

Input: global node from conceptual model.
Output: global associative object, global "string", which is a JSON of the global options

Globals are settings which affect the whole map, such as view mode (tier, map, holds...)

b) Labels:

Input: labels node
Output: Label cache

Labels are texts associated to object Ids. One object id can be a chair Id, like 1:1-3, or a stencil, like n1.
We can associate labels to object ids.

c) Objects

Input: objects node
Output: Three.js scene


*/

import simmut from 'simmut';
import {
    BufferAttribute,
    BufferGeometry,
    Color,
    Geometry,
    Line,
    LineBasicMaterial,
    Matrix4,
    Mesh,
    Object3D,
    RawShaderMaterial,
    Scene,
    Vector3,
} from 'three';
import idHelper from '../id-helper';
import venueStencilHelper from '../venue-stencil-helper/venue-stencil-helper-factory';

import {
    BACKGROUND_COLOR,
    CHAIR_FONT_SIZE,
    DEFAULT_COLOR,
    DEFAULT_FONT_SIZE,
    DEFAULT_STROKE,
    DEFAULT_STROKE_WIDTH,
    FONT_COLOR_DARK,
    FONT_COLOR_LIGHT,
    FONT_GAMMA_ZOOM_FACTOR,
    FONT_MAX_GAMMA,
    FONT_MIN_GAMMA,
    GENERAL_ADMISSION_ICON_FONT_SIZE,
    GENERAL_ADMISSION_ICON_HEIGHT,
    GENERAL_ADMISSION_STROKE_WIDTH,
    GENERAL_ADMISSION_TEXT_FONT_SIZE,
    GENERAL_ADMISSION_TEXT_HEIGHT,
    SELECTION_GRID_LINE_THICKNESS,
    SELECTION_GRID_LINE_COLOR,
    SELECTION_GRID_LINE_RENDER_ORDER,
    SELECTION_GRID_SQUARE_COLOR,
    SELECTION_GRID_SQUARE_OPACITY,
    LABEL_SIZES,
    LINE_RENDER_ORDER,
    LOCKED_CHAIR_COLOR,
    STANDARD_LABELS,
    STENCIL_COLOR,
    STENCIL_ICON_FONT_SIZE,
    STENCIL_ICON_POSITION,
    STENCIL_RENDER_ORDER,
    STENCIL_TEXT_FONT_SIZE,
    STENCIL_TEXT_POSITION,
    TABLE_COLOR,
    TABLE_RECTANGLE_STROKE_WIDTH,
    TABLE_RENDER_ORDER,
    TABLE_STROKE,
    TABLE_STROKE_WIDTH,
    TEXT_RENDER_ORDER,
    TEXT_Z,
    VENUE_STENCIL_RENDER_ORDER,
} from '../../constants/render-constants';
import colorHelper from '../colors-helper';

import { VIEW_MODE_PAS } from '../../constants/global-constants';

// Please leave commented code below for now, while still in active development
// to quickly switch between chair shaders
import chairSyncer from './customShaderChairSyncer';
import threeFontFactory from './three-helpers/three-text-helper/three-font-factory';

import ThreeTextHelper from './three-helpers/three-text-helper';

import circleFragmentShader from './shaders/circle_fragment.glsl';
import circleVertexShader from './shaders/circle_vertex.glsl';

import rectangleFragmentShader from './shaders/rectangle_fragment.glsl';
import rectangleVertexShader from './shaders/rectangle_vertex.glsl';

import icons from './icons';
// Please leave commented code below, so that we can quickly switch between
// Benton and neue-plak while debugging font performance
import stdTextFontImageData from '../../assets/webgl-fonts/neue-plak/neue-plak.datauri';
import stdTextFontDefinition from '../../assets/webgl-fonts/neue-plak/neue-plak.json';

import ticketHelper from '../ticket-helper';

import iconFontImageData from '../../assets/webgl-fonts/briteicons/briteicons.datauri';
import iconFontDefinition from '../../assets/webgl-fonts/briteicons/briteicons.json';

import {
    STATUS_AVAILABLE,
    STATUS_CARTED,
    STATUS_RESERVED,
} from '../../constants/status-constants';
import objUtils from './obj-utils';
import createTexturedRectangle from './three-helpers/textured-rectangle';

import geometryHelper from '../geometry-helper';

const { getMatrixFromProperties } = geometryHelper;

const { addObject, getObject, removeObject } = objUtils;

const { getDarkColor, getLightColor, isDark } = colorHelper;

const { getSectionIdFromUnitId } = idHelper;

class ThreeSelectorFactory {
    constructor() {
        this._scene = new Scene();
        this._updateEvent = 0;
        this._seatStatus = {};
        this._tiersCache = {};
        this._ticketClassesRef;
        this._resetSelected();
        this._resetSeatStatus();
        this._stdTextFont = threeFontFactory({
            fontDefinition: stdTextFontDefinition,
            imageData: stdTextFontImageData,
            renderOrder: TEXT_RENDER_ORDER,
        });
        this._iconFont = threeFontFactory({
            fontDefinition: iconFontDefinition,
            imageData: iconFontImageData,
            renderOrder: TEXT_RENDER_ORDER,
        });
        this._initSyncers();
    }

    _resetSelected = () => {
        this._selected = {
            ref: null,
            seats: {},
        };
    };

    _resetSeatStatus = () => {
        this._seatStatus = {
            // Cache containing the status for carted seats
            cart: {},
            cartUpdateCounter: 0,
            // Ids of the sections which contain at least one item in the
            // cart. This is important to avoid color recalculation for
            // all seats in the venue. We expect that users are not
            // carting randomly seats in hundreds of sections.
            // This is an "add only" set, so if a carted seat was ever in a section
            // that section colors will be recalculated every time a new seat
            // is carted. We need this behaviour to remove color of seats which
            // are no longer in the cart. This assumes that the user is not going to
            // be adding and removing seats from hundreds of sections, in which case
            // a small lag of up to 0.1 seconds can be expected due to seat color
            // recalculations
            cartedSections: {},
            // Cache containing the status for each seat
            statusCache: {},
            statusGlobalRef: '',
            statusRef: '',
            statusTs: 0,
            statusUpdateCounter: 0,
            usingHold: false,
        };
    };

    _getTextSize = (sizeLabel) => {
        let size = LABEL_SIZES[sizeLabel];

        if (!size) {
            size = DEFAULT_FONT_SIZE;
        }
        return size;
    };

    _createLine = ({ color, geometry }) => {
        const material = new LineBasicMaterial({ color });

        material.depthTest = false;
        material.depthWrite = false;

        const line = new Line(geometry, material);

        line.renderOrder = LINE_RENDER_ORDER;
        return line;
    };

    _syncThreeRectangle = ({
        color = DEFAULT_COLOR,
        height,
        order = STENCIL_RENDER_ORDER,
        sRectangle,
        stroke = DEFAULT_STROKE,
        strokeWidth = DEFAULT_STROKE_WIDTH,
        sx,
        sy,
        width,
        opacity = 1,
    }) => {
        let sRectangleToUse = sRectangle;

        const threeColor = new Color(color);
        const threeStrokeColor = new Color(stroke);

        if (!sRectangleToUse) {
            const createNewRectangle = () => {
                const material = new RawShaderMaterial({
                    fragmentShader: rectangleFragmentShader,
                    transparent: true,
                    uniforms: {
                        strokeWidth: { value: strokeWidth },
                        vColorAltStep: { value: 0 },
                        vColorAlt: { value: [1, 1, 1] },
                    },
                    opacity,
                    vertexShader: rectangleVertexShader,
                });

                const geometry = new BufferGeometry();

                material.depthTest = false;
                material.depthWrite = false;

                const sNewRectangle = new Mesh(geometry, material);
                // 4 vertices per rectangle,
                // 3 coordinates per vertex - x,y,z
                // 2 rectangles
                // 4*3*2 = 24
                const vertices = new Float32Array(24);

                // 3 indices per triangle
                // 2 triangles per rectangle
                // 2 rectangles
                // 3*2*2 = 12
                const indices = new Uint32Array(12);

                // 4 vertices per rectangle
                // 4 coordinates per vertex - r,g,b,a
                // 2 rectangles
                // 4*4*2=32
                const colorsArray = new Float32Array(32);
                const colorsAltArray = new Float32Array(32);

                /*
                0       1

                2       3
                */
                // outer rectangle

                indices[0] = 2;
                indices[1] = 0;
                indices[2] = 1;
                indices[3] = 3;
                indices[4] = 2;
                indices[5] = 1;

                // Indices +4 (second rectagle - inner)
                indices[6] = 2 + 4;
                indices[7] = 0 + 4;
                indices[8] = 1 + 4;
                indices[9] = 3 + 4;
                indices[10] = 2 + 4;
                indices[11] = 1 + 4;

                geometry.setAttribute(
                    'color',
                    new BufferAttribute(colorsArray, 4),
                );
                geometry.setAttribute(
                    'colorAlt',
                    new BufferAttribute(colorsAltArray, 4),
                );
                geometry.setAttribute(
                    'position',
                    new BufferAttribute(vertices, 3),
                );
                geometry.setIndex(new BufferAttribute(indices, 1));
                return sNewRectangle;
            };

            sRectangleToUse = createNewRectangle();
        }

        const geometry = sRectangleToUse.geometry;

        geometry.computeBoundingSphere();
        const verticesAttr = geometry.getAttribute('position');
        const colorsAttr = geometry.getAttribute('color');
        const colorsAltAttr = geometry.getAttribute('colorAlt');

        const verticesArray = verticesAttr.array;
        const colorsArray = colorsAttr.array;
        const colorsAltArray = colorsAltAttr.array;

        verticesAttr.needsUpdate = true;
        colorsAttr.needsUpdate = true;
        colorsAltAttr.needsUpdate = true;

        sRectangleToUse.renderOrder = order;

        verticesArray[0] = sx;
        verticesArray[1] = sy + height;
        verticesArray[2] = 0;
        verticesArray[3] = sx + width;
        verticesArray[4] = sy + height;
        verticesArray[5] = 0;
        verticesArray[6] = sx;
        verticesArray[7] = sy;
        verticesArray[8] = 0;
        verticesArray[9] = sx + width;
        verticesArray[10] = sy;
        verticesArray[11] = 0;
        //inner rectangle
        verticesArray[12] = sx + strokeWidth;
        verticesArray[13] = sy + height - strokeWidth;
        verticesArray[14] = 0;
        verticesArray[15] = sx + width - strokeWidth;
        verticesArray[16] = sy + height - strokeWidth;
        verticesArray[17] = 0;
        verticesArray[18] = sx + strokeWidth;
        verticesArray[19] = sy + strokeWidth;
        verticesArray[20] = 0;
        verticesArray[21] = sx + width - strokeWidth;
        verticesArray[22] = sy + strokeWidth;
        verticesArray[23] = 0;

        // 4 vertices per rectangle
        // 4 coordinates per color (r,g,b,a)
        // 2 rectangles
        // total: 4*4*2
        for (let i = 0; i < 4 * 4; i += 4) {
            colorsArray[i + 0] = threeStrokeColor.r;
            colorsArray[i + 1] = threeStrokeColor.g;
            colorsArray[i + 2] = threeStrokeColor.b;
            colorsArray[i + 3] = opacity;
            colorsAltArray[i + 0] = threeStrokeColor.r;
            colorsAltArray[i + 1] = threeStrokeColor.g;
            colorsAltArray[i + 2] = threeStrokeColor.b;
            colorsAltArray[i + 3] = opacity;
        }
        for (let i = 4 * 4; i < 4 * 4 * 2; i += 4) {
            colorsArray[i + 0] = threeColor.r;
            colorsArray[i + 1] = threeColor.g;
            colorsArray[i + 2] = threeColor.b;
            colorsArray[i + 3] = opacity;
            colorsAltArray[i + 0] = threeColor.r;
            colorsAltArray[i + 1] = threeColor.g;
            colorsAltArray[i + 2] = threeColor.b;
            colorsAltArray[i + 3] = opacity;
        }

        verticesAttr.needsUpdate = true;
        colorsAttr.needsUpdate = true;
        colorsAltAttr.needsUpdate = true;

        return sRectangleToUse;
    };

    _createCircle = ({
        color = DEFAULT_COLOR,
        cx,
        cy,
        height,
        order = STENCIL_RENDER_ORDER,
        stroke = DEFAULT_STROKE,
        strokeWidth = DEFAULT_STROKE_WIDTH,
        width,
    }) => {
        const sColor = new Color(color);
        const alpha = 1;
        const material = new RawShaderMaterial({
            fragmentShader: circleFragmentShader,
            transparent: true,
            uniforms: {
                strokeWidth: { value: strokeWidth },
                vColorAltStep: { value: 0 },
                vColorAlt: { value: [1, 1, 1] },
            },
            vertexShader: circleVertexShader,
        });

        material.depthTest = false;
        material.depthWrite = false;

        const geometry = new BufferGeometry();

        const sCircle = new Mesh(geometry, material);

        sCircle.renderOrder = order;

        const vertices = new Float32Array(12);
        const uvs = new Float32Array(8);
        const indices = new Uint32Array(6);

        const colorsArray = new Float32Array(4 * 4);
        const colorsAltArray = new Float32Array(4 * 4);
        const strokeArray = new Float32Array(3 * 4);

        uvs[0] = 0;
        uvs[1] = 0;
        uvs[2] = 1;
        uvs[3] = 0;
        uvs[4] = 0;
        uvs[5] = 1;
        uvs[6] = 1;
        uvs[7] = 1;

        const sx = 0.5 * width;
        const sy = 0.5 * height;

        vertices[0] = -1 * sx + cx;
        vertices[1] = 1 * sy + cy;
        vertices[2] = 0;
        vertices[3] = 1 * sx + cx;
        vertices[4] = 1 * sy + cy;
        vertices[5] = 0;
        vertices[6] = -1 * sx + cx;
        vertices[7] = -1 * sy + cy;
        vertices[8] = 0;
        vertices[9] = 1 * sx + cx;
        vertices[10] = -1 * sy + cy;
        vertices[11] = 0;

        indices[0] = 2;
        indices[1] = 0;
        indices[2] = 1;
        indices[3] = 3;
        indices[4] = 2;
        indices[5] = 1;

        colorsArray[0] = sColor.r;
        colorsArray[1] = sColor.g;
        colorsArray[2] = sColor.b;
        colorsArray[3] = alpha;
        colorsArray[4] = sColor.r;
        colorsArray[5] = sColor.g;
        colorsArray[6] = sColor.b;
        colorsArray[7] = alpha;
        colorsArray[8] = sColor.r;
        colorsArray[9] = sColor.g;
        colorsArray[10] = sColor.b;
        colorsArray[11] = alpha;
        colorsArray[12] = sColor.r;
        colorsArray[13] = sColor.g;
        colorsArray[14] = sColor.b;
        colorsArray[15] = alpha;

        for (let i = 0; i < 16; i++) {
            colorsAltArray[i] = colorsArray[i];
        }

        const strokeColor = new Color(stroke);

        strokeArray[0] = strokeColor.r;
        strokeArray[1] = strokeColor.g;
        strokeArray[2] = strokeColor.b;
        strokeArray[3] = strokeColor.r;
        strokeArray[4] = strokeColor.g;
        strokeArray[5] = strokeColor.b;
        strokeArray[6] = strokeColor.r;
        strokeArray[7] = strokeColor.g;
        strokeArray[8] = strokeColor.b;
        strokeArray[9] = strokeColor.r;
        strokeArray[10] = strokeColor.g;
        strokeArray[11] = strokeColor.b;

        geometry.setAttribute('color', new BufferAttribute(colorsArray, 4));
        geometry.setAttribute(
            'colorAlt',
            new BufferAttribute(colorsAltArray, 4),
        );
        geometry.setAttribute('position', new BufferAttribute(vertices, 3));
        geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
        geometry.setAttribute('stroke', new BufferAttribute(strokeArray, 3));
        geometry.setIndex(new BufferAttribute(indices, 1));

        return sCircle;
    };

    _removeUnused = (obj, updateEvent) => {
        for (let i = obj.children.length - 1; i >= 0; i--) {
            const child = obj.children[i];

            if (
                !child.userData.weak &&
                child.userData.updateEvent !== updateEvent
            ) {
                obj.remove(child);
            }
        }
    };

    _syncLabel = ({
        backgroundColor = BACKGROUND_COLOR,
        childName = 'label',
        font,
        fontSize = DEFAULT_FONT_SIZE,
        label,
        rotate,
        sParent,
        x,
        y,
    }) => {
        const labelX = x;
        const labelY = y;
        // Label has precedence over id
        // If we cannot find the label for a specific id, then
        // we will use the id instead. That should not happen in a well formed
        // map, but we understand that it is better to display the id than not to
        // display anything
        const labelToUse = label || '';
        const sObjectToReturn = sParent;

        let sLabel = getObject(sObjectToReturn, childName);

        if (labelX !== undefined && labelY !== undefined && labelToUse) {
            if (!sLabel) {
                sLabel = new ThreeTextHelper({ font });
                sLabel.userData.weak = true;
                sLabel.renderOrder = TEXT_RENDER_ORDER;
                addObject(sObjectToReturn, childName, sLabel);
            }
            // We only create/update the text if it is different from what we set last time
            if (sLabel.userData.label !== labelToUse) {
                sLabel.clear();
                const isThisTextDark = isDark(backgroundColor);

                sLabel.addText({
                    alignmentX: 'center',
                    alignmentY: 'center',
                    color: isThisTextDark ? FONT_COLOR_LIGHT : FONT_COLOR_DARK,
                    fontSize,
                    rotate,
                    targetBox: {
                        max: {
                            x: labelX,
                            y: labelY,
                        },
                        min: {
                            x: labelX,
                            y: labelY,
                        },
                    },
                    text: labelToUse,
                    z: TEXT_Z,
                });
                sLabel.updateGeometry();
                sLabel.userData.label = labelToUse;
            }
        } else if (sLabel) {
            removeObject(sObjectToReturn, 'label', sLabel);
        }
    };

    _syncCircleObject = ({
        color,
        height,
        id,
        order,
        sObject,
        sParent,
        stroke,
        strokeWidth,
        sx,
        sy,
        width,
        x,
        y,
    }) => {
        let sObjectToReturn = sObject;

        if (!sObjectToReturn) {
            sObjectToReturn = this._createCircle({
                color,
                cx: sx,
                cy: sy,
                height,
                order,
                stroke,
                strokeWidth,
                width,
            });
            addObject(sParent, id, sObjectToReturn);
        }

        const childMatrix = new Matrix4();

        childMatrix.setPosition(new Vector3(x, y, 0));
        sObjectToReturn.matrix.copy(childMatrix);
        sObjectToReturn.matrixAutoUpdate = false;

        return sObjectToReturn;
    };

    _syncTexturedRectangle = ({
        height,
        id,
        order,
        sObject,
        sParent,
        shape,
        sx,
        sy,
        venueStencils,
        width,
        x,
        y,
    }) => {
        let sObjectToReturn = sObject;

        if (!sObjectToReturn) {
            const venueStencilId = venueStencils[shape];
            const venueStencilData =
                venueStencilHelper.getStencilData(venueStencilId);
            const { imageData, maxU, maxV, minU, minV } = venueStencilData;

            sObjectToReturn = createTexturedRectangle({
                height,
                imageData,
                maxU,
                maxV,
                minU,
                minV,
                order,
                sx,
                sy,
                width,
            });

            addObject(sParent, id, sObjectToReturn);
        }

        const childMatrix = new Matrix4();

        childMatrix.setPosition(new Vector3(x, y, 0));
        sObjectToReturn.matrix.copy(childMatrix);
        sObjectToReturn.matrixAutoUpdate = false;

        return sObjectToReturn;
    };

    _syncRectangleObject = ({
        color,
        height,
        id,
        order,
        sObject,
        sParent,
        stroke,
        strokeWidth,
        sx,
        sy,
        width,
        x,
        y,
    }) => {
        const sObjectToReturn = this._syncThreeRectangle({
            color,
            height,
            order,
            sRectangle: sObject,
            stroke,
            strokeWidth,
            sx,
            sy,
            width,
        });

        if (!sObject) {
            addObject(sParent, id, sObjectToReturn);
        }

        const childMatrix = new Matrix4();

        childMatrix.setPosition(new Vector3(x, y, 0));
        sObjectToReturn.matrix.copy(childMatrix);
        sObjectToReturn.matrixAutoUpdate = false;

        return sObjectToReturn;
    };

    _syncLineObject = ({ id, sObject, sParent, x, x2, y, y2 }) => {
        let sObjectToReturn = sObject;

        if (!sObjectToReturn) {
            const newGeometry = new Geometry();

            sObjectToReturn = this._createLine({
                color: DEFAULT_COLOR,
                geometry: newGeometry,
            });
            addObject(sParent, id, sObjectToReturn);
        }
        const geometry = sObjectToReturn.geometry;

        geometry.vertices.length = 0;
        geometry.verticesNeedUpdate = true;

        geometry.vertices.push(new Vector3(x, y, 0));
        geometry.vertices.push(new Vector3(x2, y2, 0));

        return sObjectToReturn;
    };

    _syncGenericObject = ({ properties, sObject, sParent }) => {
        const id = properties.id;

        let sObjectToReturn = sObject;

        if (!sObjectToReturn) {
            sObjectToReturn = new Object3D();
            addObject(sParent, id, sObjectToReturn);
        }

        return sObjectToReturn;
    };

    _getRecursiveChairs = (node) => {
        let chairs = [];

        let headers = [];

        if (node) {
            const chairsValue = node.chairs;

            if (chairsValue) {
                chairs = [chairsValue];
                const properties = node.properties;

                headers = properties;
            }
            const contentsNode = node.contents;

            if (contentsNode) {
                const childNames = Object.keys(contentsNode);

                if (childNames && childNames.length) {
                    childNames.forEach((childName) => {
                        const child = contentsNode[childName];
                        const result = this._getRecursiveChairs(child);

                        if (result) {
                            chairs = chairs.concat(result.chairs);
                            headers = headers.concat(result.headers);
                        }
                    });
                }
            }
        }

        return {
            chairs,
            headers,
        };
    };

    _checkMustUpdateColors = ({
        globalsNode,
        id,
        seatStatus,
        selected,
        userData,
    }) => {
        const userDataToUse = userData;

        let mustUpdateColors = false;

        if (!userData) {
            return true;
        }
        if (userDataToUse.globalsRef !== globalsNode) {
            userDataToUse.globalsRef = globalsNode;
            const hoverObject = globalsNode.hoverObject || '';
            const hoverId = globalsNode.hoverId;
            const hovered = hoverObject === id;

            /*
                We have to minimise the amount of repaintings, therefore we have two variables:
                - hoverObject
                - hoverId

                hoverObject --> Which section we are hovering on
                hoverId --> Which seat we are hovering on

                We have to avoid repainting all sections when hovering on a seat. Therefore each section has
                two variables: hovered and hoverId

                hover indicates if we are hovering on this section. When there is a change, we have to repaint
                hoverId indicates which seat we are hovering on. When there is a change in the seat, we only have
                to repaint the section where the id is.

                Below condition triggers:

                - When we enter or leave a section
                - When we are hovering in a section and we change the seat being hovered on
            */
            if (
                hovered !== userDataToUse.hovered ||
                (hovered && hoverId !== userDataToUse.hoverId)
            ) {
                userDataToUse.hovered = hovered;
                userDataToUse.hoverId = hoverId;
                mustUpdateColors = true;
            }

            if (seatStatus) {
                if (
                    seatStatus.statusUpdateCounter !==
                    userDataToUse.statusUpdateCounter
                ) {
                    userDataToUse.statusUpdateCounter =
                        seatStatus.statusUpdateCounter;
                    mustUpdateColors = true;
                }
                if (
                    seatStatus.cartUpdateCounter !==
                    userDataToUse.cartUpdateCounter
                ) {
                    userDataToUse.cartUpdateCounter =
                        seatStatus.cartUpdateCounter;
                    if (
                        seatStatus.cartedSections &&
                        seatStatus.cartedSections[id]
                    ) {
                        mustUpdateColors = true;
                    }
                }
            }
        }

        if (
            userDataToUse.tiersRef !== this._tiersRef ||
            userDataToUse.ticketClassesRef !== this._ticketClassesRef
        ) {
            userDataToUse.tiersRef = this._tiersRef;
            userDataToUse.ticketClassesRef = this._ticketClassesRef;
            mustUpdateColors = true;
        }
        if (userDataToUse.selectionRef !== selected.ref) {
            userDataToUse.selectionRef = selected.ref;
            mustUpdateColors = true;
        }
        return mustUpdateColors;
    };

    _syncContainerObject = ({
        backgroundColor,
        globalsNode,
        noLabel,
        node,
        properties,
        sObject,
        sParent,
        seatStatus,
        selected,
        updateEvent,
        manageMode,
    }) => {
        const {
            hideSectionLabel,
            id,
            l: label,
            label_x: labelX,
            label_y: labelY,
            rowLabelVisibility,
        } = properties;

        let sObjectToReturn = sObject;

        let mustUpdateColors = false;

        if (!sObjectToReturn) {
            sObjectToReturn = new Object3D();
            addObject(sParent, id, sObjectToReturn);
        }

        const userData = sObjectToReturn.userData;

        if (userData.ref !== node) {
            mustUpdateColors = true;
            const childMatrix = getMatrixFromProperties({ properties });

            sObjectToReturn.matrix.copy(childMatrix);
            sObjectToReturn.matrixAutoUpdate = false;

            if (!hideSectionLabel && !noLabel) {
                this._syncLabel({
                    backgroundColor,
                    childName: 'label',
                    font: this._stdTextFont,
                    id,
                    label,
                    sParent: sObjectToReturn,
                    x: labelX,
                    y: labelY,
                });
            }

            const contentsRef = node.contents || node.chairs;

            if (contentsRef !== sObjectToReturn.userData.contentsRef) {
                sObjectToReturn.userData.contentsRef = contentsRef;

                const { chairs, headers } = this._getRecursiveChairs(
                    node,
                    [],
                    [],
                );

                sObjectToReturn.userData.chairs = chairs;
                sObjectToReturn.userData.headers = headers;

                chairSyncer.syncChairs({
                    chairs,
                    chairsHash: sObjectToReturn.userData.contentsRef,
                    font: this._stdTextFont,
                    fontSize: CHAIR_FONT_SIZE,
                    globalsNode,
                    headers,
                    id,
                    rowLabelVisibility,
                    sParent: sObjectToReturn,
                    seatStatus,
                    selected,
                    tiers: this._tiersCache,
                    updateEvent,
                });
            }
        }

        mustUpdateColors =
            mustUpdateColors ||
            this._checkMustUpdateColors({
                globalsNode,
                id,
                selected,
                seatStatus,
                userData,
            });

        if (mustUpdateColors) {
            chairSyncer.syncChairColors({
                globalsNode,
                id,
                sParent: sObjectToReturn,
                seatStatus,
                selected,
                tiers: this._tiersCache,
                manageMode,
            });
        }

        return sObjectToReturn;
    };

    _syncTableAndChair = ({
        globalsNode,
        node,
        properties,
        sObject,
        sParent,
        seatStatus,
        selected,
        updateEvent,
        manageMode,
    }) => {
        let tableH;

        let tableW;

        let tableR;

        let sChildShape;

        const sObjectToReturn = this._syncContainerObject({
            backgroundColor: TABLE_COLOR,
            globalsNode,
            node,
            properties,
            sObject,
            sParent,
            seatStatus,
            selected,
            updateEvent,
            manageMode,
        });

        const userData = sObjectToReturn.userData;

        if (userData.ref !== node) {
            sChildShape = sObjectToReturn.getObjectByName('tableShape');

            // We will add the shape
            const tableType = properties.tableType;

            if (tableType === 'circle') {
                tableR = +properties['table_r'];
                sChildShape = this._syncCircleObject({
                    color: TABLE_COLOR,
                    height: 2 * tableR,
                    id: 'tableShape',
                    order: TABLE_RENDER_ORDER,
                    sObject: sChildShape,
                    sParent: sObjectToReturn,
                    stroke: TABLE_STROKE,
                    strokeWidth: TABLE_STROKE_WIDTH,
                    sx: 0,
                    sy: 0,
                    type: properties.tableType,
                    width: 2 * tableR,
                    x: 0,
                    y: 0,
                });
            } else {
                tableH = +properties['table_h'];
                tableW = +properties['table_w'];
                sChildShape = this._syncRectangleObject({
                    color: TABLE_COLOR,
                    height: tableH,
                    id: 'tableShape',
                    order: TABLE_RENDER_ORDER,
                    sObject: sChildShape,
                    sParent: sObjectToReturn,
                    stroke: TABLE_STROKE,
                    strokeWidth: TABLE_RECTANGLE_STROKE_WIDTH,
                    sx: -tableW * 0.5,
                    sy: -tableH * 0.5,
                    type: properties.tableType,
                    width: tableW,
                    x: 0,
                    y: 0,
                });
            }
            sChildShape.userData.weak = true;
        }

        return sObjectToReturn;
    };

    _syncLineStencil = ({ node, properties, sObject, sParent }) => {
        let sObjectToReturn = sObject;
        const userData = sObjectToReturn ? sObjectToReturn.userData : null;

        if (!sObjectToReturn || (userData && userData.ref !== node)) {
            const x = +properties.x;
            const y = +properties.y;
            const x2 = +properties.x2;
            const y2 = +properties.y2;
            const id = properties.id;

            sObjectToReturn = this._syncLineObject({
                id,
                sObject,
                sParent,
                x,
                x2,
                y,
                y2,
            });
        }

        return sObjectToReturn;
    };

    _getGAColors = ({ hovered, statusRecord = {}, t, usingHold }) => {
        let color = DEFAULT_COLOR;

        let stroke = getDarkColor(DEFAULT_COLOR);

        let availableTickets = 0;

        let protectedTickets = 0;

        let variants;

        const { S = {} } = statusRecord;

        availableTickets = S.A;
        protectedTickets = S.P;

        const tier = this._tiersCache[t];

        if (tier) {
            variants = tier.variants;
            if (tier.color) {
                color = tier.color;
            }
        }

        let locked = false;
        // If no ticket is available for GA (hidden tickets), we set it as locked

        if (!variants || !variants.length) {
            locked = true;
        }

        if (usingHold && !protectedTickets) {
            locked = true;
        }
        if (!usingHold && !availableTickets) {
            locked = true;
        }

        if (locked) {
            color = LOCKED_CHAIR_COLOR;
            stroke = LOCKED_CHAIR_COLOR;
        } else if (hovered) {
            stroke = getLightColor(color);
        } else {
            stroke = getDarkColor(color);
        }
        return {
            color,
            stroke,
        };
    };

    _getGALabels = ({
        color,
        globalsNode,
        hideSectionLabel,
        id,
        labelText,
        sObject,
        seatingType,
        statusRecord,
    }) => {
        const labels = [];

        let penPosY = 0;

        if (seatingType && icons[seatingType]) {
            labels.push({
                backgroundColor: color,
                childName: 'text_icon',
                font: this._iconFont,
                fontSize: GENERAL_ADMISSION_ICON_FONT_SIZE,
                label: icons[seatingType],
                sParent: sObject,
                x: 0,
                y: penPosY,
            });
            penPosY += GENERAL_ADMISSION_ICON_HEIGHT;
        }

        if (!hideSectionLabel) {
            labels.push({
                backgroundColor: color,
                childName: 'text_label',
                font: this._stdTextFont,
                fontSize: GENERAL_ADMISSION_TEXT_FONT_SIZE,
                label: labelText || id,
                sParent: sObject,
                x: 0,
                y: penPosY,
            });
            penPosY += GENERAL_ADMISSION_TEXT_HEIGHT;
        }

        if (statusRecord && statusRecord.S) {
            const usingHold = globalsNode.usingHold;
            const showGAAvailability = globalsNode.showGAAvailability;

            let capacity = 0;

            if (usingHold) {
                capacity = statusRecord.S.P;
            } else {
                capacity = statusRecord.S.A;
            }

            if (capacity && showGAAvailability) {
                const label = `Available: ${capacity}`;

                labels.push({
                    backgroundColor: color,
                    childName: 'text_capacity',
                    font: this._stdTextFont,
                    fontSize: GENERAL_ADMISSION_TEXT_FONT_SIZE,
                    label,
                    sParent: sObject,
                    x: 0,
                    y: penPosY,
                });
                penPosY += GENERAL_ADMISSION_TEXT_HEIGHT;
            }
        }
        return labels;
    };

    _syncGeneralAdmission = ({
        globalsNode,
        node,
        properties,
        sObject,
        sParent,
        selected,
        seatStatus,
    }) => {
        const {
            height,
            hideSectionLabel,
            id,
            l: labelText,
            seatingType,
            shape,
            t,
            width,
        } = properties;

        let sObjectToReturn = sObject;
        const hoverObject = globalsNode.hoverObject || '';
        const hovered = hoverObject === id;

        if (!sObjectToReturn) {
            sObjectToReturn = new Object3D();
            addObject(sParent, id, sObjectToReturn);
        }

        const userData = sObjectToReturn.userData;

        const checkRebuild = () => {
            let rebuild = false;

            if (userData && userData.ref !== node) {
                userData.ref = node;
                rebuild = true;
            } else if (hovered !== userData.hovered) {
                userData.hovered = hovered;
                rebuild = true;
            }
            const mustUpdateColors = this._checkMustUpdateColors({
                globalsNode,
                id,
                seatStatus,
                selected,
                userData,
            });

            return rebuild || mustUpdateColors;
        };

        const mustRebuild = checkRebuild();

        if (mustRebuild) {
            const statusCache = this._seatStatus.statusCache;
            const usingHold = this._seatStatus.usingHold;
            const statusRecord = statusCache[id];

            const { color, stroke } = this._getGAColors({
                hovered,
                statusRecord,
                t,
                usingHold,
            });

            const childMatrix = getMatrixFromProperties({ properties });

            sObjectToReturn.matrix.copy(childMatrix);
            sObjectToReturn.matrixAutoUpdate = false;

            sObjectToReturn.userData = {
                ...sObjectToReturn.userData,
                userData,
            };

            let shapeObject = getObject(sObjectToReturn, 'shape');

            switch (shape) {
                case 'circle':
                    shapeObject = this._syncCircleObject({
                        color,
                        height: +properties.height,
                        id,
                        order: STENCIL_RENDER_ORDER,
                        sObject: shapeObject,
                        sParent: sObjectToReturn,
                        stroke,
                        strokeWidth: 0,
                        sx: +properties.sx,
                        sy: +properties.sy,
                        width: +properties.width,
                        x: 0,
                        y: 0,
                    });
                    break;
                default:
                    shapeObject = this._syncRectangleObject({
                        color,
                        height: +properties.height,
                        id,
                        order: STENCIL_RENDER_ORDER,
                        sObject: shapeObject,
                        sParent: sObjectToReturn,
                        stroke,
                        strokeWidth: GENERAL_ADMISSION_STROKE_WIDTH,
                        sx: +properties.sx - width * 0.5,
                        sy: +properties.sy - height * 0.5,
                        type: properties.shape,
                        width: +properties.width,
                        x: 0,
                        y: 0,
                    });
                    break;
            }

            addObject(sObjectToReturn, 'shape', shapeObject);
            shapeObject.userData.weak = true;
            // Add interactivity to the object
            const sx = +properties.sx - width * 0.5;
            const sy = +properties.sy - height * 0.5;

            shapeObject.userData = {
                ...shapeObject.userData,
                boundingBox: {
                    max: {
                        x: sx + width,
                        y: sy + height,
                    },
                    min: {
                        x: sx,
                        y: sy,
                    },
                },
                boxList: new Float32Array([sx, sy, sx + width, sy + height]),
                referrer: {
                    idToIndex: { [id]: 0 },
                    ids: [id],
                    type: 'ga',
                },
            };

            const labels = this._getGALabels({
                color,
                globalsNode,
                hideSectionLabel,
                id,
                labelText,
                sObject: shapeObject,
                seatingType,
                statusRecord,
            });

            if (labels.length) {
                const lastPenPosY = labels[labels.length - 1].y;
                const centerY = 0.5 * lastPenPosY;

                labels.forEach((label) => {
                    const labelToModify = label;

                    labelToModify.y -= centerY;
                    this._syncLabel(label);
                });
            }
        }

        return sObjectToReturn;
    };

    _syncVenueStencil = ({
        globalsNode,
        node,
        properties,
        sObject,
        sParent,
        updateEvent,
        selected,
        venueStencils,
    }) => {
        let sObjectToReturn = sObject;
        const userData = sObjectToReturn ? sObjectToReturn.userData : null;

        if (!sObjectToReturn || (userData && userData.ref !== node)) {
            const width = +properties.width;
            const height = +properties.height;

            sObjectToReturn = this._syncContainerObject({
                backgroundColor: STENCIL_COLOR,
                globalsNode,
                node,
                properties,
                sObject,
                sParent,
                selected,
                updateEvent,
            });

            const { stencil } = properties;

            let shapeObject = getObject(sParent, 'shape');

            shapeObject = this._syncTexturedRectangle({
                height: +properties.height,
                id: 'shape',
                order: VENUE_STENCIL_RENDER_ORDER,
                sObject: shapeObject,
                sParent: sObjectToReturn,
                shape: stencil,
                sx: -width * 0.5,
                sy: -height * 0.5,
                type: properties.shape,
                venueStencils,
                width: +properties.width,
                x: 0,
                y: 0,
            });

            shapeObject.userData.weak = true;
        }

        return sObjectToReturn;
    };

    _syncStencil = ({
        globalsNode,
        node,
        properties,
        sObject,
        sParent,
        selected,
        updateEvent,
    }) => {
        let sObjectToReturn = sObject;
        const userData = sObjectToReturn ? sObjectToReturn.userData : null;

        if (!sObjectToReturn || (userData && userData.ref !== node)) {
            const width = +properties.width;
            const height = +properties.height;

            sObjectToReturn = this._syncContainerObject({
                backgroundColor: STENCIL_COLOR,
                globalsNode,
                node,
                properties,
                selected,
                sObject,
                sParent,
                updateEvent,
            });

            const { id, label: stencilLabel, shape, stencil } = properties;

            let labelX;

            let labelY;

            let shapeObject = getObject(sObjectToReturn, 'shape');

            const shapeToUse = shape || stencil;

            switch (shapeToUse) {
                case 'circle':
                    shapeObject = this._syncCircleObject({
                        color: STENCIL_COLOR,
                        height: +properties.height,
                        id,
                        order: STENCIL_RENDER_ORDER,
                        sObject: shapeObject,
                        sParent: sObjectToReturn,
                        stroke: STENCIL_COLOR,
                        strokeWidth: 0,
                        sx: +properties.sx,
                        sy: +properties.sy,
                        width: +properties.width,
                        x: 0,
                        y: 0,
                    });
                    labelX = +properties.sx;
                    labelY = +properties.sy;
                    break;
                default:
                    shapeObject = this._syncRectangleObject({
                        color: STENCIL_COLOR,
                        height: +properties.height,
                        id,
                        order: STENCIL_RENDER_ORDER,
                        sObject: shapeObject,
                        sParent: sObjectToReturn,
                        stroke: STENCIL_COLOR,
                        strokeWidth: 0,
                        sx: +properties.sx - width * 0.5,
                        sy: +properties.sy - height * 0.5,
                        type: properties.shape,
                        width: +properties.width,
                        x: 0,
                        y: 0,
                    });
                    labelX = +properties.sx;
                    labelY = +properties.sy;
                    break;
            }
            const iconText = icons[stencil];
            const font = this._stdTextFont;

            if (iconText) {
                if (typeof window !== 'undefined') {
                    window.debugIconFont = this._iconFont;
                }
                const labelParams = {
                    backgroundColor: STENCIL_COLOR,
                    childName: 'icon_label',
                    font: this._iconFont,
                    fontSize: STENCIL_ICON_FONT_SIZE,
                    label: iconText,
                    sParent: shapeObject,
                    x: labelX + STENCIL_ICON_POSITION.x,
                    y: labelY + STENCIL_ICON_POSITION.y,
                };

                this._syncLabel(labelParams);

                const stencilLabelToUse =
                    stencilLabel || STANDARD_LABELS[stencil];

                this._syncLabel({
                    backgroundColor: STENCIL_COLOR,
                    childName: 'text_label',
                    font,
                    fontSize: STENCIL_TEXT_FONT_SIZE,
                    label: stencilLabelToUse,
                    sParent: shapeObject,
                    x: labelX + STENCIL_TEXT_POSITION.x,
                    y: labelY + STENCIL_TEXT_POSITION.y,
                });
            }

            addObject(sObjectToReturn, 'shape', shapeObject);
            shapeObject.userData.weak = true;
        }

        return sObjectToReturn;
    };

    _syncStaticText = ({
        globalsNode,
        node,
        properties,
        sObject,
        sParent,
        selected,
        updateEvent,
    }) => {
        let sObjectToReturn = sObject;
        const userData = sObjectToReturn ? sObjectToReturn.userData : null;

        if (!sObjectToReturn || (userData && userData.ref !== node)) {
            const { fontSize, label } = properties;

            sObjectToReturn = this._syncContainerObject({
                backgroundColor: BACKGROUND_COLOR,
                globalsNode,
                node,
                properties,
                sObject,
                selected,
                sParent,
                updateEvent,
            });
            const textSize = this._getTextSize(fontSize);

            this._syncLabel({
                backgroundColor: BACKGROUND_COLOR,
                childName: 'label',
                font: this._stdTextFont,
                fontSize: textSize,
                label,
                rotate: true,
                sParent: sObjectToReturn,
                x: 0,
                y: 0,
            });
        }

        return sObjectToReturn;
    };

    _initSyncers = () => {
        this._syncers = {
            default: {
                fnc: this._syncGenericObject,
                loopChildren: true,
            },
            general_admission_section: {
                fnc: this._syncGeneralAdmission,
                loopChildren: false,
            },
            line_stencil: {
                fnc: this._syncLineStencil,
                loopChildren: false,
            },
            section: {
                fnc: this._syncContainerObject,
                loopChildren: false,
            },
            static_text: {
                fnc: this._syncStaticText,
                loopChildren: false,
            },
            stencil: {
                fnc: this._syncStencil,
                loopChildren: false,
            },
            tableAndChair: {
                fnc: this._syncTableAndChair,
                loopChildren: false,
            },
            venue_stencil: {
                fnc: this._syncVenueStencil,
                loopChildren: false,
            },
        };
    };

    _syncElementRecur = (
        sParent,
        node,
        globalsNode,
        seatStatus,
        selected,
        venueStencils,
        updateEvent,
        manageMode,
    ) => {
        // safeguard
        if (!node || !sParent) {
            return;
        }

        const contents = node.contents;
        const properties = node.properties || {};

        const type = properties.type;
        const id = properties.id;
        const deleted = properties.deleted;

        let sObject = getObject(sParent, id);

        if (!deleted) {
            if (
                !sObject ||
                sObject.userData.ref !== node ||
                sObject.userData.globalRef !== globalsNode
            ) {
                let syncer = this._syncers[type];

                if (!syncer) {
                    syncer = this._syncers.default;
                }
                const loopChildren = syncer.loopChildren;

                sObject = syncer.fnc({
                    globalsNode,
                    node,
                    properties,
                    sObject,
                    sParent,
                    seatStatus,
                    selected,
                    updateEvent,
                    venueStencils,
                    manageMode,
                });

                if (loopChildren && contents) {
                    const childNames = Object.keys(contents);

                    childNames.map((childName) => {
                        const child = contents[childName];

                        if (child) {
                            this._syncElementRecur(
                                sObject,
                                child,
                                globalsNode,
                                seatStatus,
                                selected,
                                venueStencils,
                                updateEvent,
                                manageMode,
                            );
                        }
                        return undefined;
                    });
                }
                // After syncing all children, we have to removed unused children
                this._removeUnused(sObject, updateEvent);
                sObject.userData.ref = node;
                sObject.userData.id = id;
                sObject.userData.globalRef = globalsNode;
            }
            // We mark object as checked
            sObject.userData.updateEvent = updateEvent;
        }
    };

    _syncObjects = ({
        globalsNode,
        node,
        seatStatus,
        selected,
        venueStencils,
        manageMode,
    }) => {
        this._updateEvent++;

        this._syncElementRecur(
            this._scene,
            node,
            globalsNode,
            seatStatus,
            selected,
            venueStencils,
            this._updateEvent,
            manageMode,
        );

        this._removeUnused(this._scene, this._updateEvent);
    };

    _syncGrid = ({ interactive, worldToPixelsFactor }) => {
        const grid = interactive.grid || {};
        const { startPos, curPos } = grid;

        const prevGridObj = getObject(this._scene, 'selectionGrid');

        if (!prevGridObj || prevGridObj.userData.gridRef !== interactive.grid) {
            let startPosToUse = { x: 0, y: 0 };
            let curPosToUse = { x: 0, y: 0 };
            let visible = false;

            if (startPos && curPos) {
                startPosToUse = {
                    x: Math.min(startPos.x, curPos.x),
                    y: Math.min(startPos.y, curPos.y),
                };
                curPosToUse = {
                    x: Math.max(startPos.x, curPos.x),
                    y: Math.max(startPos.y, curPos.y),
                };
                visible = true;
            }

            const gridObjToUse = this._syncThreeRectangle({
                sRectangle: prevGridObj,
                color: SELECTION_GRID_SQUARE_COLOR,
                height: curPosToUse.y - startPosToUse.y,
                width: curPosToUse.x - startPosToUse.x,
                order: SELECTION_GRID_LINE_RENDER_ORDER,
                stroke: SELECTION_GRID_LINE_COLOR,
                strokeWidth:
                    SELECTION_GRID_LINE_THICKNESS / worldToPixelsFactor,
                opacity: SELECTION_GRID_SQUARE_OPACITY,
                sx: startPosToUse.x,
                sy: startPosToUse.y,
            });

            if (!prevGridObj) {
                addObject(this._scene, 'selectionGrid', gridObjToUse);
            }

            gridObjToUse.visible = visible;
            gridObjToUse.userData.weak = true;
            gridObjToUse.userData.gridRef = interactive.grid;
        }
    };

    _resetTiersCache = () => {
        this._tiersCache = {};
        this._tiersRef = '';
    };

    _syncSelected = ({ globalsNode }) => {
        const globalselected = globalsNode.selected;

        if (!globalselected || globalselected !== this._selected.ref) {
            this._resetSelected();
            this._selected.ref = globalselected;

            if (
                globalselected &&
                globalselected.seats &&
                globalselected.seats.length
            ) {
                for (let i = 0; i < globalselected.seats.length; i++) {
                    const seatId = globalselected.seats[i];
                    const sectionId = getSectionIdFromUnitId(seatId);

                    this._selected.seats[seatId] = true;
                    // We also set as selected the section so that we can quickly identify sections to resync
                    this._selected.seats[sectionId] = true;
                }
            }
        }
    };

    _syncTiers = ({ node }) => {
        const tiersNode = node.tiers;

        if (!tiersNode) {
            if (this._tiersRef) {
                this._resetTiersCache();
            }
            return;
        }

        const ticketClasses = simmut.get(node, 'globals.ticketClasses');
        const globals = simmut.get(node, 'globals') || {};

        if (
            tiersNode !== this._tiersRef ||
            ticketClasses !== this._ticketClassesRef
        ) {
            this._ticketClassesRef = ticketClasses;
            Object.keys(tiersNode).forEach((tierId) => {
                const tierNode = tiersNode[tierId];

                if (tierNode) {
                    const color = tierNode.color;
                    const tier = +tierId;

                    // eslint-disable-next-line no-restricted-globals
                    if (!isNaN(tier)) {
                        const variants = ticketHelper.getVariantsFromTier({
                            globals,
                            tier,
                        });

                        this._tiersCache[tierId] = {
                            color,
                            variants,
                        };
                    }
                }
            });
            this._tiersRef = tiersNode;
        }
    };

    /*
    _statusCache: Contains information about seat status, obtained from multiple sources
    For a specific id, like 1-3:8, it contains
    {
        ss: // Status as it comes from the server
        s: // Final status that we will use for painting. Calculated from ss and user selections
    }

    */
    _ensureStatusRecordForId = (statusCache, id) => {
        const statusCacheToUse = statusCache;

        if (!statusCacheToUse[id]) {
            statusCacheToUse[id] = {
                //server status
                s: '',
                ss: '',
                t: 0,
            };
        }
    };

    // Updates the seat status from the statusId which changed
    // This is important for the change streamer
    _updateStatusFromChangedIds = (statusCache, changedIds) => {
        const statusCacheToUse = statusCache;

        for (let i = 0; i < changedIds.length; i++) {
            const unitId = changedIds[i];

            if (!unitId) {
                continue;
            }
            const record = statusCacheToUse[unitId];
            // Attendee status (cart) has priority over server status

            record.s = record.ss;
        }
    };

    // Overwrites the status coming from server with the status coming from the
    // cart if seats are carted. If they are not carted or have no carting status
    // we keep the server status (this is needed to return to original status for
    // seats that changed from carted to available)
    _mergeStatusWithCart = (seatStatus, cart) => {
        const seatStatusToUse = seatStatus;
        const statusCache = seatStatusToUse.statusCache;
        const statusTs = seatStatus.statusTs || 0;
        const cartedSections = seatStatusToUse.cartedSections || {};

        if (cart) {
            Object.keys(cart).forEach((unitId) => {
                if (unitId) {
                    const sectionId = getSectionIdFromUnitId(unitId);

                    cartedSections[sectionId] = true;
                    const statusRecord = statusCache[unitId];
                    // Attendee status (cart) has priority over server status

                    if (statusRecord) {
                        const cartRecord = cart[unitId];

                        if (cartRecord.status === STATUS_CARTED) {
                            statusRecord.s = cartRecord.status;
                        } else if (cartRecord && cartRecord.ts > statusTs) {
                            statusRecord.s = cartRecord.status;
                        }
                    }
                }
            });
        }
        seatStatusToUse.cartedSections = cartedSections;
    };

    _syncStatusCart = ({ globalsNode, seatStatus, holds }) => {
        const seatStatusToUse = seatStatus;
        const { ids: holdIds = {} } = holds;

        if (globalsNode && seatStatusToUse.statusGlobalRef !== globalsNode) {
            seatStatusToUse.statusGlobalRef = globalsNode;

            const globalCart = globalsNode.cart || null;

            if (seatStatusToUse.globalCartRef !== globalCart) {
                seatStatusToUse.globalCartRef = globalCart;
                const cart = seatStatusToUse.cart || {};

                // Just in case some seats are no longer in the cart,
                // we mark all seats in the cart as available, and then
                // they will be overwritten by the selected chairs. We do not
                // change the ts though
                Object.keys(cart).forEach((key) => {
                    if (holdIds[key]) {
                        cart[key].status = STATUS_RESERVED;
                    } else {
                        cart[key].status = STATUS_AVAILABLE;
                    }
                });

                if (globalCart) {
                    Object.keys(globalCart).forEach((id) => {
                        const unitStatus = simmut.get(
                            globalsNode,
                            `cart.${id}.status`,
                        );
                        const ts = simmut.get(globalsNode, `cart.${id}.ts`);

                        cart[id] = {
                            status: unitStatus,
                            ts,
                        };
                    });
                }

                seatStatusToUse.cart = cart;
                seatStatusToUse.cartUpdateCounter++;
                this._mergeStatusWithCart(seatStatusToUse, cart);
            }
        }
    };

    _syncStatus = ({ globalsNode, statusNode, holds }) => {
        const statusRef = this._seatStatus.statusRef;

        let statusCache = this._seatStatus.statusCache;

        if (!statusNode) {
            if (statusRef) {
                this._resetSeatStatus();
            }
            return;
        }
        const changedIds = [];

        if (statusRef !== statusNode) {
            this._seatStatus.statusRef = statusNode;
            this._seatStatus.statusUpdateCounter =
                (this._seatStatus.statusUpdateCounter || 0) + 1;
            statusCache = {};
            this._seatStatus.statusCache = statusCache;
            const usingHold = globalsNode.usingHold;
            const statusTs = globalsNode.statusTs;

            this._seatStatus.statusTs = statusTs;
            this._seatStatus.usingHold = !!usingHold;

            Object.keys(statusNode).forEach((statusId) => {
                const container = statusNode[statusId] || {};
                const s = container.s || [];
                const S = container.S;

                if (S !== undefined) {
                    statusCache[statusId] = {
                        S,
                    };
                }
                for (let i = 0; i < s.length; i++) {
                    const id = `${statusId}:${i + 1}`;

                    this._ensureStatusRecordForId(statusCache, id);
                    statusCache[id].ss = s[i];

                    changedIds.push(id);
                }
            });
            const seatCount = Object.keys(statusCache).length;

            console.log('Total number of ids: ', seatCount);
        }

        if (changedIds.length) {
            this._updateStatusFromChangedIds(
                this._seatStatus.statusCache,
                changedIds,
            );
        }

        this._syncStatusCart({
            globalsNode,
            seatStatus: this._seatStatus,
            holds,
        });
    };

    _reportTime = (t, msg) => {
        if (t > 20) {
            console.log(`Long time: ${t} in ${msg}`);
        }
    };

    _getGammaFromZoom = ({ zoom }) => {
        let gamma = FONT_GAMMA_ZOOM_FACTOR / zoom;

        if (gamma < FONT_MIN_GAMMA) {
            gamma = FONT_MIN_GAMMA;
        }
        if (gamma > FONT_MAX_GAMMA) {
            gamma = FONT_MAX_GAMMA;
        }
        return gamma;
    };

    getScene = ({ camera, node = {}, worldToPixelsFactor }) => {
        const objectsNode = node.objects || {};
        const statusNode = node.status || {};
        const globalsNode = node.globals || {};
        const interactive = node.interactive || {};
        const holds = node.holds || {};
        const venueStencils = node['venue_stencils'] || {};
        const manageMode = globalsNode.viewMode !== VIEW_MODE_PAS;

        const t0 = Date.now();

        this._syncTiers({ node });
        const t1 = Date.now();

        this._syncStatus({ globalsNode, statusNode, holds });
        this._syncSelected({ globalsNode });

        const t2 = Date.now();

        this._syncObjects({
            camera,
            globalsNode,
            node: objectsNode,
            seatStatus: this._seatStatus,
            selected: this._selected,
            venueStencils,
            manageMode,
        });

        this._syncGrid({ interactive, worldToPixelsFactor });

        const t3 = Date.now();

        this._reportTime(t1 - t0, 'Tier sync');
        this._reportTime(t2 - t1, 'Status sync');
        this._reportTime(t3 - t2, 'Object sync');

        if (camera) {
            const zoom = camera.zoom;
            const gamma = this._getGammaFromZoom({ zoom });

            this._stdTextFont.setGamma(gamma);
            this._iconFont.setGamma(gamma);
        }

        return this._scene;
    };
}

export default ThreeSelectorFactory;
